import { noItx } from "../../../src/translationItx/itx";
// import initialModelSample from "../data/samples/efh-1.json";

import initialModelSample from "../data/samples/from-bitmap.json";

import { generateAutoOpenings } from "./C1_autoOpenings";
import { scaleAxesToBuildingDimensions } from "./C1_transformBuildingDimensions";
import { C1_State } from "../model/C1_State";
import c1_state from "../model/C1_State";
import { C1_Project } from "../model/C1_Project";
import { C1_DEFAULT_MODEL_SETTINGS, C1_ModelSettings } from "../model/C1_ModelSettings";
import { C1_Floor } from "../model/C1_Floor";
import type { C1_EquipmentStandard, C1_HouseType } from "../types/C1_ModelTypes";
import { createEmptyProject } from "../../../src/DemoData";
import ProjectMetadata from "../../../src/models/ProjectMetadata";
import Floor from "../../../src/models/Floor";

export interface C1_BuildingDimensions {
  houseType: C1_HouseType;
  width: number;
  length: number;
  floorDistance: number;
  equipmentStandard: C1_EquipmentStandard;
}

const defaultBuildingDimensions: C1_BuildingDimensions = {
  houseType: "detachedHouse",
  width: 12,
  length: 12,
  floorDistance: 3,
  equipmentStandard: 4,
};

export function initialModel(): C1_State {
  return modelFromSample(initialModelSample);
}

export function emptyModel(
  projectName: string,
  dimensions: C1_BuildingDimensions = defaultBuildingDimensions,
  options: { forPdfImport?: boolean } = {},
): C1_State {
  console.info(noItx("[C1 Create] Entering emptyModel"), { projectName, dimensions });
  const mainState = globalThis.state;
  mainState.suppressUndoObservation = true;
  console.info(noItx("[C1 Create] Creating project metadata"));
  const metadata = new ProjectMetadata();
  console.info(noItx("[C1 Create] Setting user for new project"));
  metadata.setUserForNewProject();
  metadata.name = projectName;
  metadata.description = "Concept One";
  metadata.isC1Project = true;
  metadata.isEstadaProject = false;
  mainState.projectMetadata = metadata;
  console.info(noItx("[C1 Create] Metadata set; initializing empty ESTADA project"), { metadataId: metadata.id });
  createEmptyProject(mainState);
  mainState.suppressUndoObservation = true;
  console.info(noItx("[C1 Create] Empty ESTADA project initialized"), {
    floorOwnerCount: mainState.floors.size,
    axisCount: mainState.axes.size,
  });

  const model = c1_state;

  console.info(noItx("[C1 Create] Creating C1_Project"));
  const project = new C1_Project({
    id: metadata.id,
    updatedAt: new Date().toISOString(),
    northAngle: 0,
    houseType: dimensions.houseType,
    buildingWidth: dimensions.width,
    buildingLength: dimensions.length,
    floorDistance: dimensions.floorDistance,
    equipmentStandard: dimensions.equipmentStandard,
    selectedProductManufacturers: {},
  }, mainState.templateGroups);
  console.info(noItx("[C1 Create] C1_Project created"), { projectId: project.id });

  console.info(noItx("[C1 Create] Initializing project settings"));
  project.settings = new C1_ModelSettings(C1_DEFAULT_MODEL_SETTINGS, project);
  console.info(noItx("[C1 Create] Initializing project products"));
  console.info(noItx("[C1 Create] Creating default axes"));
  project.replaceAxes(options.forPdfImport ? [] : [
    { kind: "v", pos: 0, label: "A" },
    { kind: "v", pos: dimensions.width, label: "B" },
    { kind: "h", pos: 0, label: "1" },
    { kind: "h", pos: dimensions.length, label: "2" },
  ]);
  console.info(noItx("[C1 Create] Default axes created"), { axisCount: mainState.axes.size });
  mainState.c1Project = project;
  console.info(noItx("[C1 Create] C1_Project set in global state"));

  console.info(noItx("[C1 Create] Creating default floor"));
  if (options.forPdfImport) {
    model._currentFloorOwner = undefined;
    model.currentFloorId.value = "";
  } else {
    const floor = project.createDefaultFloor();
    console.info(noItx("[C1 Create] Default floor created"), { floorId: floor.id, floorName: (floor.owner?.name ?? "") });
    model.setCurrentFloor(floor.id);
  }
  // New projects always start with building editing, regardless of the previous project.
  model.editMode.value = "building";
  model.roomDialogDocked.value = true;
  model.roomDialogOpen.value = !options.forPdfImport;
  console.info(noItx("[C1 Create] Current floor set"), { currentFloorId: model.currentFloorId.value });
  mainState.enableUndoMonitoring();
  mainState.suppressUndoObservation = false;
  mainState.undoManager.clear();
  console.info(noItx("[C1 Create] emptyModel completed"));
  return model;
}

export function modelFromSample(
  input: unknown,
  projectName?: string,
  dimensions: C1_BuildingDimensions = defaultBuildingDimensions,
): C1_State {
  const sample = structuredClone(input) as any;
  const model = emptyModel(projectName ?? sample.projectName ?? "", dimensions);
  globalThis.state.suppressUndoObservation = true;
  const json = {
    houseType: dimensions.houseType,
    buildingWidth: dimensions.width,
    buildingLength: dimensions.length,
    floorDistance: dimensions.floorDistance,
    equipmentStandard: dimensions.equipmentStandard,
    selectedProductManufacturers: sample.selectedProductManufacturers ?? {},
    northAngle: sample.northAngle ?? 0,
    settings: { ...C1_DEFAULT_MODEL_SETTINGS, ...sample.settings },
    axes: scaleAxesToBuildingDimensions(sample.axes ?? [], dimensions.width, dimensions.length),
  };
  model.project.fromJSON(json);
  const importedAxisPositions = new Map<string, number>();
  for (const axis of json.axes) importedAxisPositions.set(axis.id, axis.pos);

  globalThis.state.floors.clear();
  const floorIds = new Map<string, string>();
  for (const floorData of sample.floors ?? []) {
    const floor = new Floor({ name: floorData.name }, globalThis.state.floors);
    floor.c1 = new C1_Floor(
      {
        level: floorData.level ?? 0,
        ownCabinet: floorData.ownCabinet === true,
      },
      floor,
    );
    globalThis.state.floors.add(floor);
    if (floorData.id) floorIds.set(floorData.id, floor.id);

    for (const sourceRoom of floorData.rooms ?? []) {
      const roomOpenings =
        sourceRoom.openings ?? (floorData.openings ?? []).filter((opening: any) => opening.roomId === sourceRoom.id);
      const {
        id: _roomId,
        furnitures = [],
        openings: _openings,
        left,
        right,
        top,
        bottom,
        ...roomData
      } = sourceRoom;
      const x1 = importedAxisPositions.get(left) ?? 0;
      const x2 = importedAxisPositions.get(right) ?? x1 + 1;
      const y1 = importedAxisPositions.get(top) ?? 0;
      const y2 = importedAxisPositions.get(bottom) ?? y1 + 1;
      const room = floor.c1.addRoom({
        ...roomData,
        startPoint: { x: x1, y: y1 },
        rect: { x1, x2, y1, y2 },
      });
      for (const sourceFurniture of furnitures) {
        const { id: _furnitureId, ...furnitureData } = sourceFurniture;
        room.addFurniture(furnitureData);
      }
      for (const sourceOpening of roomOpenings) {
        const { id: _openingId, roomId: _roomId, ...openingData } = sourceOpening;
        room.addOpening(openingData);
      }
    }
  }

  model.project.floors.forEach((floor) => {
    if (!floor.rooms.some((room) => room.openings.length > 0)) {
      model.currentFloorId.value = floor.id;
      const openings = generateAutoOpenings(floor);
      floor.rooms.forEach((room) => room.replaceOpenings(openings.filter((opening) => opening.roomId === room.id)));
    }
  });

  model.setCurrentFloor(floorIds.get(sample.currentFloorId) ?? model.floors[0]?.id ?? "");

  globalThis.state.enableUndoMonitoring();
  globalThis.state.suppressUndoObservation = false;
  globalThis.state.undoManager.clear();
  return model;
}
